import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RawMath } from "@/components/math-text";
import type { DerivationContent } from "../content-types";
import { Explanation, Prompt } from "./shared";
import type { QuestionRendererProps } from "./types";

interface PoolEntry {
  token: string;
  text: string;
  isCorrect: boolean;
}

// Static class strings per tint so Tailwind's JIT can see the full names.
interface TintStyle {
  box: string;
  fg: string;
}
const TINT: Record<"pink" | "emerald" | "red", TintStyle> = {
  pink: { box: "border-pink-300 bg-pink-50/70", fg: "text-pink-600" },
  emerald: { box: "border-emerald-300 bg-emerald-50/70", fg: "text-emerald-600" },
  red: { box: "border-red-300 bg-red-50/70", fg: "text-red-600" },
};

function shuffled<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** derivation — mirror of iOS DerivationView. Show the target statement and a
 *  shuffled pool of correct steps mixed with distractors; the learner taps
 *  steps to build an ordered derivation. Tokens are `correct-<index>` /
 *  `distractor-<n>`. Grading (in grading.ts) compares the picked step TEXTS to
 *  the canonical order; any distractor makes it wrong. */
export function DerivationRenderer({
  content,
  answer,
  setAnswer,
  revealed,
}: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as DerivationContent;

  const pool: PoolEntry[] = useMemo(() => {
    const entries: PoolEntry[] = [];
    for (const step of c.steps ?? [])
      entries.push({ token: `correct-${step.index}`, text: step.text, isCorrect: true });
    (c.distractors ?? []).forEach((text, i) =>
      entries.push({ token: `distractor-${i}`, text, isCorrect: false }),
    );
    return shuffled(entries);
  }, [c.steps, c.distractors]);

  const byToken = useMemo(() => {
    const m = new Map<string, PoolEntry>();
    for (const e of pool) m.set(e.token, e);
    return m;
  }, [pool]);

  const picked: string[] = (answer as string[]) ?? [];
  const isPicked = (token: string) => picked.includes(token);

  const toggle = (token: string) => {
    if (revealed) return;
    setAnswer(isPicked(token) ? picked.filter((tk) => tk !== token) : [...picked, token]);
  };

  return (
    <div>
      <Prompt>{c.prompt}</Prompt>

      {/* Target statement — the result the derivation should reach. */}
      <div className="mt-3 rounded-xl bg-pink-50 p-3.5">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-pink-600">
          {t("Target")}
        </p>
        <RawMath text={c.target_statement} className="text-base" />
      </div>

      {/* User's chosen sequence. */}
      {picked.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold text-ink-faint">{t("Your derivation")}</p>
          <ol className="space-y-1.5">
            {picked.map((token, idx) => {
              const entry = byToken.get(token);
              const isDistractor = token.startsWith("distractor-");
              const tint: TintStyle = revealed
                ? isDistractor
                  ? TINT.red
                  : TINT.emerald
                : TINT.pink;
              return (
                <li
                  key={token}
                  className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 text-sm ${tint.box}`}
                >
                  <span className={`font-bold tabular-nums ${tint.fg}`}>{idx + 1}.</span>
                  <span className="flex-1">
                    <RawMath text={entry?.text ?? "?"} />
                  </span>
                  {!revealed ? (
                    <button
                      type="button"
                      onClick={() => toggle(token)}
                      className="text-ink-faint hover:text-red-500"
                      aria-label={t("Remove")}
                    >
                      ✕
                    </button>
                  ) : (
                    <span className={tint.fg}>{isDistractor ? "✕" : "✓"}</span>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {/* Pool of available steps. */}
      <div className="mt-4">
        <p className="mb-2 text-xs font-semibold text-ink-faint">
          {picked.length === 0
            ? t("Tap steps in derivation order")
            : t("Tap to add the next step")}
        </p>
        <div className="space-y-1.5">
          {pool.map((item) => {
            const chosen = isPicked(item.token);
            // After reveal, a missing correct step is highlighted so the
            // learner sees what they should have selected.
            const missingCorrect = revealed && !chosen && item.isCorrect;
            return (
              <button
                key={item.token}
                type="button"
                disabled={revealed}
                onClick={() => toggle(item.token)}
                className={`flex w-full items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-sm transition disabled:cursor-default ${
                  chosen
                    ? "border-pink-400 bg-pink-50/70 opacity-60"
                    : missingCorrect
                      ? "border-emerald-400 bg-emerald-50"
                      : "border-black/10 bg-white hover:bg-pink-50/50"
                }`}
              >
                <span className={chosen ? "text-pink-500" : "text-ink-faint"}>
                  {chosen ? "◉" : "○"}
                </span>
                <span className="flex-1">
                  <RawMath text={item.text} />
                </span>
                {missingCorrect && <span className="text-emerald-600">←</span>}
              </button>
            );
          })}
        </div>
      </div>

      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}
